import {
  type JobBoardMessage,
  type OfferedJob,
} from '@hotlist/shared';

/**
 * The work board.
 *
 * The civilian half of the game, and the thing that was missing: without it a
 * player who is not enforcement has a car, a map and no reason to drive
 * anywhere. Take a job, drive it, get paid.
 *
 * The turn is that doing this lays down exactly the sort of legible weekly
 * pattern the other side reads. Nothing here says so. The audit log does not
 * explain itself either, and for the same reason.
 *
 * Illegal work is labeled as illegal, plainly, because a player choosing it
 * should know what they chose -- and no more than that. There is no warning and
 * no confirmation.
 */
export interface WorkHandlers {
  take: (id: string) => void;
  /** "I am here." Collects on the first leg, delivers on the second. */
  arrive: () => void;
  abandon: () => void;
  buy: (what: 'registration' | 'vehicle' | 'fuel') => void;
  /** Show the player where a job goes. */
  route: (node: number) => void;
}

export class WorkPanel {
  private readonly list: HTMLElement;
  private readonly current: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly handlers: WorkHandlers;
  private board: JobBoardMessage | null = null;
  private atDrop = false;

  constructor(handlers: WorkHandlers) {
    this.handlers = handlers;
    this.list = must('work-list');
    this.current = must('work-current');
    this.purse = must('work-purse');

    this.list.addEventListener('click', (event) => {
      const id = (event.target as HTMLElement).dataset.take;
      if (id) handlers.take(id);
    });

    this.current.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.dataset.finish !== undefined) handlers.arrive();
      else if (target.dataset.abandon !== undefined) handlers.abandon();
      else if (target.dataset.go !== undefined) handlers.route(Number(target.dataset.go));
    });

    this.purse.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const go = target.dataset.go;
      if (go) {
        handlers.route(Number(go));
        return;
      }
      const what = target.dataset.buy;
      if (what === 'registration' || what === 'vehicle' || what === 'fuel') handlers.buy(what);
    });
  }

  show(board: JobBoardMessage, arrived = false): void {
    this.board = board;
    this.paintPurse(board);
    this.paintCurrent(board, arrived);
    this.paintBoard(board);
  }

  /**
   * Repaint when the car arrives, so "hand it in" appears the moment it can be
   * pressed rather than the next time the board happens to refresh.
   */
  setAtDrop(arrived: boolean): void {
    if (arrived === this.atDrop) return;
    this.atDrop = arrived;
    if (this.board) this.paintCurrent(this.board, arrived);
  }

  private paintPurse(board: JobBoardMessage): void {
    this.purse.replaceChildren();

    const money = document.createElement('div');
    money.className = 'work-money';
    money.textContent = `$${board.money}`;
    this.purse.append(money);

    if (board.wanted) {
      // Stated once, plainly, and not explained. What it means for somebody
      // whose week is already in the detection table is the thing to work out.
      const wanted = document.createElement('div');
      wanted.className = 'work-wanted';
      wanted.textContent = 'your plate is on a list';
      this.purse.append(wanted);
    }

    if (board.earnedLegal + board.earnedIllegal > 0) {
      const earned = document.createElement('div');
      earned.className = 'work-earned';
      // Split out because the split is the whole of what a player chose. Stated
      // and not scored.
      earned.textContent =
        board.earnedIllegal > 0
          ? `earned $${board.earnedLegal} straight, $${board.earnedIllegal} not`
          : `earned $${board.earnedLegal}`;
      this.purse.append(earned);
    }

    // Renewing a tag and buying a car used to be here, because this is where
    // the money is counted. That is the wrong reason to put a control
    // somewhere: money is what you pay with, not what you are buying, and both
    // of those are things about a vehicle. They are in the garage now.
  }

  /**
   * The job in hand.
   *
   * Three buttons crammed into one row cut the third one off on a narrow
   * console, and "hand in $88" offered from four miles away read like a
   * cancel button. So: the drive is its own full-width action, handing in only
   * appears where it can actually be done, and giving up says giving up.
   */
  private paintCurrent(board: JobBoardMessage, arrived: boolean): void {
    this.current.replaceChildren();
    const job = board.current;
    if (!job) return;

    const collecting = board.stage !== 'deliver';
    const target = board.target;

    const what = document.createElement('div');
    what.className = job.illegal ? 'work-current-what illegal' : 'work-current-what';
    what.textContent = job.description;
    this.current.append(what);

    if (arrived && target) {
      const act = document.createElement('button');
      act.type = 'button';
      act.className = 'work-finish';
      act.dataset.finish = '';
      // "Hand it in" read like handing over the money you had earned. You are
      // collecting a thing, then delivering it; the money comes to you.
      act.textContent = collecting
        ? `collect from ${short(target.name)}`
        : `deliver — collect $${job.pays}`;
      this.current.append(act);
    } else if (target) {
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'work-go';
      go.dataset.go = String(target.node);
      go.textContent = collecting
        ? `collect at ${short(target.name)}`
        : `deliver to ${short(target.name)}`;
      this.current.append(go);

      const where = document.createElement('div');
      where.className = 'work-current-where';
      where.textContent = collecting
        ? `pick it up at ${target.name}, ${target.town}`
        : `${target.name}, ${target.town} — $${job.pays} on delivery`;
      this.current.append(where);
    }

    const drop = document.createElement('button');
    drop.type = 'button';
    drop.className = 'work-abandon';
    drop.dataset.abandon = '';
    drop.textContent = 'give up this job';
    this.current.append(drop);
  }

  private paintBoard(board: JobBoardMessage): void {
    this.list.replaceChildren();

    if (board.current) {
      const note = document.createElement('div');
      note.className = 'query-empty';
      note.textContent = 'One job at a time.';
      this.list.append(note);
      return;
    }

    if (board.jobs.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'query-empty';
      empty.textContent = 'No work going.';
      this.list.append(empty);
      return;
    }

    for (const job of board.jobs) {
      const row = document.createElement('div');
      row.className = job.illegal ? 'work-row illegal' : 'work-row';

      const where = document.createElement('span');
      where.className = 'work-where';
      where.textContent = `${job.fromName} → ${job.toName}`;

      const pays = document.createElement('span');
      pays.className = 'work-pays';
      pays.textContent = `$${job.pays}`;

      const take = document.createElement('button');
      take.type = 'button';
      take.className = 'work-take';
      take.dataset.take = job.id;
      take.textContent = job.illegal ? 'take (illegal)' : 'take';

      row.append(where, pays, take);
      this.list.append(row);
    }
  }
}

/**
 * A venue name that fits on a button.
 *
 * Real businesses have real names, and some of them are "Village of Elm Grove
 * Department of Public Works". Cut rather than wrapped, because a button that
 * grows to three lines pushes everything under it off a phone.
 */
function short(name: string, limit = 22): string {
  return name.length <= limit ? name : `${name.slice(0, limit - 1).trimEnd()}…`;
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
